import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Grid, Typography, Button, Chip, Stack,
  Table, TableHead, TableRow, TableCell, TableBody, Divider,
  IconButton, Tooltip, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import SavingsIcon from '@mui/icons-material/Savings';
import PercentIcon from '@mui/icons-material/Percent';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link, useHistory } from 'react-router-dom';
import apiService from '../../services/apiServices';
import PageHeader from '../common/PageHeader';

const fmtEur = (n) =>
  n === null || n === undefined || n === ''
    ? 'Pa kufi'
    : new Intl.NumberFormat('sq-AL', { style: 'currency', currency: 'EUR' }).format(Number(n));

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('sq-AL'); } catch { return d; }
};

function BracketTable({ brackets }) {
  if (!brackets?.length) return (
    <Typography variant="caption" color="text.secondary">Asnjë shkallë tatimore</Typography>
  );
  return (
    <Table size="small" sx={{ mt: 1 }}>
      <TableHead>
        <TableRow sx={{ bgcolor: 'action.hover' }}>
          <TableCell sx={{ py: 0.5, fontWeight: 700, fontSize: 12 }}>Nga (€)</TableCell>
          <TableCell sx={{ py: 0.5, fontWeight: 700, fontSize: 12 }}>Deri (€)</TableCell>
          <TableCell sx={{ py: 0.5, fontWeight: 700, fontSize: 12 }}>Tatimi %</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {brackets.map((b, i) => (
          <TableRow key={i}>
            <TableCell sx={{ py: 0.5, fontSize: 12 }}>{fmtEur(b.MinAmount ?? b.From ?? b.range_from ?? 0)}</TableCell>
            <TableCell sx={{ py: 0.5, fontSize: 12 }}>{fmtEur(b.MaxAmount ?? b.To ?? b.range_to)}</TableCell>
            <TableCell sx={{ py: 0.5, fontSize: 12 }}>
              <Chip size="small" label={`${b.Percent ?? b.Tax ?? b.tax_rate ?? 0}%`} color="info" variant="outlined" />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export default function FiscalPoliticsList() {
  const history = useHistory();
  const [policies, setPolicies]     = useState([]);
  const [brackets, setBrackets]     = useState({});
  const [loading, setLoading]       = useState(true);
  const [deleteId, setDeleteId]     = useState(null);
  const [toast, setToast]           = useState('');
  const [toastType, setToastType]   = useState('success');

  const showToast = (msg, type = 'success') => {
    setToast(msg); setToastType(type);
    setTimeout(() => setToast(''), 3500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ext, det] = await Promise.all([
        apiService().get('fiscalPoliciesExt'),
        apiService().get('fiscalPoliticsDetails'),
      ]);
      const pList = ext?.success ? (ext.data || []) : [];
      const dList = det?.success ? (det.data || []) : [];
      setPolicies(pList.sort((a, b) => (b.Active ? 1 : 0) - (a.Active ? 1 : 0)));
      const map = {};
      dList.forEach(d => {
        const pid = d.FiscalPoliticsId ?? d.fiscalPoliticsId ?? d.policyId;
        if (pid) { if (!map[pid]) map[pid] = []; map[pid].push(d); }
      });
      setBrackets(map);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    if (!deleteId) return;
    // Serveri e refuzon fshirjen e një politike aktive ose të përdorur (409) —
    // dhe përgjigja vjen si përjashtim. Pa këtë kapje rreshti hiqej nga ekrani
    // sikur të ishte fshirë, dhe kthehej i gjallë te rifreskimi i parë.
    try {
      await apiService().delete('fiscalPoliciesExt', deleteId);
      setPolicies(prev => prev.filter(p => Number(p.Id) !== Number(deleteId)));
      setDeleteId(null);
      showToast('Politika u fshi me sukses.');
    } catch (e) {
      setDeleteId(null);
      showToast(e?.message || 'Fshirja nuk u krye.', 'error');
    }
  };

  const handleToggleActive = async (policy) => {
    const updated = { ...policy, Active: !policy.Active };
    await apiService().put('fiscalPoliciesExt', updated);
    setPolicies(prev => prev.map(p => Number(p.Id) === Number(policy.Id) ? updated : p));
    showToast(updated.Active ? 'Politika u aktivizua.' : 'Politika u çaktivizua.');
  };

  return (
    <Box>
      <PageHeader
        title="Politikat Fiskale"
        actions={
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => history.goBack()}>
              Kthehu
            </Button>
            <Button component={Link} to="/fiscal/addfiscal" variant="contained" startIcon={<AddIcon />}>
              Politikë e re
            </Button>
          </Stack>
        }
      />

      {toast && (
        <Alert severity={toastType} sx={{ mb: 2 }} onClose={() => setToast('')}>{toast}</Alert>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        <strong>Kosovë — LP Nr. 03/L-212 + Ligji Nr. 08/L-142:</strong> Pension 5%+5% · Tatim (nga rroga e shtatorit 2024): 0–250€→0% | 250–450€→8% | &gt;450€→10% · Paga min: 264€ · Maks orë shtesë: 8h/javë (Neni 23) · Shtesa: 30% jashtë orarit/natë, 50% fundjavë/festa, 20% kujdestari (Neni 56)
      </Alert>

      {loading ? (
        <Typography color="text.secondary">Duke ngarkuar…</Typography>
      ) : policies.length === 0 ? (
        <Alert severity="warning">
          Asnjë politikë fiskale. Klikoni <strong>"Politikë e re"</strong> për të filluar.
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {policies.map(policy => {
            const pBrackets = brackets[policy.Id] || [];
            return (
              <Grid item xs={12} md={6} key={policy.Id}>
                <Card sx={{
                  borderRadius: 2,
                  border: policy.Active ? '2px solid #3E8FA3' : '1px solid #e2e8f0',
                  position: 'relative',
                }}>
                  {policy.Active && (
                    <Chip
                      label="AKTIVE"
                      color="primary"
                      size="small"
                      icon={<CheckCircleIcon />}
                      sx={{ position: 'absolute', top: 12, right: 12 }}
                    />
                  )}
                  <CardContent>
                    <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 1.5 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight={800}>
                          {policy.Name || `Politika #${policy.Id}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {fmtDate(policy.ValidFrom)} → {policy.ValidTo ? fmtDate(policy.ValidTo) : 'Pa datë mbarimi'}
                        </Typography>
                      </Box>
                    </Stack>

                    <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                      <Grid item xs={6}>
                        <Box sx={{ bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200', borderRadius: 1.5, p: 1.5, textAlign: 'center' }}>
                          <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
                            <SavingsIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                            <Typography variant="caption" fontWeight={700} color="primary.main">Pension Punëtor</Typography>
                          </Stack>
                          <Typography variant="h5" fontWeight={900} color="primary.main">
                            {policy.PensionEmployee ?? 5}%
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200', borderRadius: 1.5, p: 1.5, textAlign: 'center' }}>
                          <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
                            <SavingsIcon sx={{ fontSize: 16, color: 'success.main' }} />
                            <Typography variant="caption" fontWeight={700} color="success.main">Pension Punëdhënës</Typography>
                          </Stack>
                          <Typography variant="h5" fontWeight={900} color="success.main">
                            {policy.PensionEmployer ?? 5}%
                          </Typography>
                        </Box>
                      </Grid>
                      {policy.MinimumWage && (
                        <Grid item xs={6}>
                          <Box sx={{ bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.200', borderRadius: 1.5, p: 1, textAlign: 'center' }}>
                            <Typography variant="caption" fontWeight={700} color="warning.dark">Paga minimale</Typography>
                            <Typography variant="h6" fontWeight={800} color="warning.dark">{policy.MinimumWage}€</Typography>
                          </Box>
                        </Grid>
                      )}
                      {(policy.MaxOvertimeHoursWeek || policy.MaxOvertimeHours) && (
                        <Grid item xs={6}>
                          <Box sx={{ bgcolor: 'error.50', border: '1px solid', borderColor: 'error.200', borderRadius: 1.5, p: 1, textAlign: 'center' }}>
                            <Typography variant="caption" fontWeight={700} color="error.dark">Maks orë shtesë/javë</Typography>
                            <Typography variant="h6" fontWeight={800} color="error.dark">{policy.MaxOvertimeHoursWeek ?? policy.MaxOvertimeHours}h/javë</Typography>
                          </Box>
                        </Grid>
                      )}
                      {policy.OvertimeRate !== undefined && (
                        <Grid item xs={12}>
                          <Box sx={{ bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200', borderRadius: 1.5, p: 1 }}>
                            <Typography variant="caption" fontWeight={700} color="text.secondary">Shtesa (Neni 56): </Typography>
                            <Typography variant="caption">
                              Jashtë orarit: <strong>{policy.OvertimeRate ?? 30}%</strong> &nbsp;|&nbsp;
                              Natë: <strong>{policy.NightWorkRate ?? 30}%</strong> &nbsp;|&nbsp;
                              Fundjavë: <strong>{policy.WeekendRate ?? 50}%</strong> &nbsp;|&nbsp;
                              Festë: <strong>{policy.HolidayRate ?? 50}%</strong> &nbsp;|&nbsp;
                              Kujdestari: <strong>{policy.OnCallRate ?? 20}%</strong>
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                    </Grid>

                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <PercentIcon sx={{ fontSize: 14 }} /> Shkallët tatimore ({pBrackets.length})
                    </Typography>
                    <BracketTable brackets={pBrackets} />

                    <Stack direction="row" spacing={1} sx={{ mt: 2 }} justifyContent="flex-end">
                      <Tooltip title={policy.Active ? 'Çaktivizo' : 'Aktivizo'}>
                        <Button
                          size="small"
                          variant="outlined"
                          color={policy.Active ? 'warning' : 'success'}
                          startIcon={policy.Active ? <RadioButtonUncheckedIcon /> : <CheckCircleIcon />}
                          onClick={() => handleToggleActive(policy)}
                        >
                          {policy.Active ? 'Çaktivizo' : 'Aktivizo'}
                        </Button>
                      </Tooltip>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon />}
                        component={Link}
                        to={`/fiscal/editfiscal/${policy.Id}`}
                      >
                        Edito
                      </Button>
                      <Tooltip title={policy.Active ? 'Politika aktive nuk fshihet — aktivizo një tjetër së pari' : 'Fshi politikën (nuk lejohet nëse ka llogaritur paga)'}>
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setDeleteId(policy.Id)}
                            disabled={policy.Active}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Konfirmo fshirjen</DialogTitle>
        <DialogContent>
          <Typography>A jeni i sigurt që doni të fshini këtë politikë fiskale? Ky veprim nuk kthehet mbrapsht.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Anulo</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Fshi</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
